// ==================== Imports ====================
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import logo from '../Images/swapiify.png';
import { apiFetch } from '../utils/api';


// ==================== Register Component ====================
export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',   // final combined address yahi save hoga (backend ke liye)
    password: '',
  });

  // address ke parts sirf UI ke liye
  const [addressParts, setAddressParts] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: '',
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Handle simple inputs (name, email, phone, password)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Address parts change -> UI + combined address
  const handleAddressChange = (field, value) => {
    // ✅ Basic guard: allowed chars per field
    let v = value;

    if (field === 'pincode') {
      // sirf digits allow + length limit (6)
      v = value.replace(/\D/g, '').slice(0, 6);
    }

    if (field === 'city' || field === 'state') {
      // sirf letters + spaces
      v = value.replace(/[^a-zA-Z\s]/g, '');
    }

    setAddressParts(prev => {
      const updated = { ...prev, [field]: v };

      // full address string bana ke formData.address me daal rahe
      const combined = [
        updated.line1,
        updated.line2,
        updated.city,
        updated.state,
        updated.pincode,
      ]
        .map(part => (part || '').trim())
        .filter(Boolean)
        .join(', ');

      setFormData(prev => ({
        ...prev,
        address: combined,   // backend ko yehi jayega
      }));

      return updated;
    });
  };

  // Register logic (BACKEND SAME)
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await apiFetch('/register', {
        method: 'POST',
        body: JSON.stringify(formData),   // address already combined
      });

      setMessage('✅ Registration successful!');
      setTimeout(() => navigate('/login'), 1000);
    } catch (err) {
      console.error('Register error:', err);

      const errMsg =
        err?.body?.message ||
        err?.body?.error ||
        err?.message ||
        '❌ This user already exists';

      setMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.logo}>
            <img src={logo} alt="Swapiify Logo" style={styles.logoImage} />
          </div>
          <h2 style={styles.heading}>Create Your Account</h2>
          <p style={styles.subtitle}>Join Swapiify today — it's quick and easy!</p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleRegister} style={styles.form}>
          {/* Name */}
          <div style={styles.inputGroup}>
            <label htmlFor="name" style={styles.label}>Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={handleChange}
              required
              style={styles.input}
              disabled={loading}
            />
          </div>

          {/* Email */}
          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.label}>Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              required
              style={styles.input}
              disabled={loading}
            />
          </div>

          {/* Phone */}
          <div style={styles.inputGroup}>
            <label htmlFor="phone" style={styles.label}>Phone Number</label>
            <input
              type="text"
              id="phone"
              name="phone"
              placeholder="Enter your phone number"
              value={formData.phone}
              onChange={handleChange}
              required
              style={styles.input}
              disabled={loading}
            />
          </div>

          {/* Address – multiple boxes but combined in one field */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>Address</label>

            {/* Line 1 */}
            <div style={styles.addressRow}>
              <div style={styles.addressColFull}>
                <input
                  type="text"
                  placeholder="Flat / House no. & Street"
                  value={addressParts.line1}
                  onChange={(e) => handleAddressChange('line1', e.target.value)}
                  required
                  style={styles.input}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Line 2 */}
            <div style={styles.addressRow}>
              <div style={styles.addressColFull}>
                <input
                  type="text"
                  placeholder="Area / Locality / Landmark"
                  value={addressParts.line2}
                  onChange={(e) => handleAddressChange('line2', e.target.value)}
                  required
                  style={styles.input}
                  disabled={loading}
                />
              </div>
            </div>

            {/* City + State + Pincode */}
            <div style={styles.addressRow}>
              <div style={styles.addressCol}>
                <input
                  type="text"
                  placeholder="City"
                  value={addressParts.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  required
                  style={styles.input}
                  disabled={loading}
                />
              </div>
              <div style={styles.addressCol}>
                <input
                  type="text"
                  placeholder="State"
                  value={addressParts.state}
                  onChange={(e) => handleAddressChange('state', e.target.value)}
                  required
                  style={styles.input}
                  disabled={loading}
                />
              </div>
              <div style={styles.addressColSmall}>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Pincode"
                  value={addressParts.pincode}
                  onChange={(e) => handleAddressChange('pincode', e.target.value)}
                  required
                  style={styles.input}
                  disabled={loading}
                />
              </div>
            </div>

            <div style={styles.hintText}>
              Your full address will be stored as one line, same as before.
            </div>
          </div>

          {/* Password */}
          <div style={styles.inputGroup}>
            <label htmlFor="password" style={styles.label}>Password</label>
            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleChange}
                required
                style={styles.passwordInput}
                disabled={loading}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                style={styles.passwordToggle}
                disabled={loading}
              >
                {showPassword ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
          >
            {loading ? (
              <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                Registering...
              </div>
            ) : (
              'Sign Up'
            )}
          </button>

          {/* Message */}
          {message && (
            <div
              style={{
                ...styles.message,
                ...(message.includes('✅') ? styles.messageSuccess : styles.messageError),
              }}
            >
              {message}
            </div>
          )}
        </form>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>
            Already have an account?{' '}
            <Link to="/login" style={styles.link}>
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// ==================== Styles (same theme as Login) ====================
const styles = {
  container: {
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    padding: '10px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: '460px',
    background: 'white',
    borderRadius: '7px',
    padding: '48px 40px',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e5e7eb',
  },
  header: { textAlign: 'center', marginBottom: '32px' },
  logoImage: { width: '200px' },
  logo: { marginBottom: '24px' },
  heading: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#495057',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#878a99',
    margin: '0',
    lineHeight: '1.5',
  },
  form: { marginBottom: '24px' },
  inputGroup: { marginBottom: '18px' },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#495057',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  // address layout
  addressRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  addressColFull: {
    flex: 1,
  },
  addressCol: {
    flex: 1,
  },
  addressColSmall: {
    flexBasis: '30%',
  },
  hintText: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#6c757d',
  },
  passwordContainer: { position: 'relative', display: 'flex', alignItems: 'center' },
  passwordInput: {
    width: '100%',
    padding: '12px 45px 12px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
  },
  passwordToggle: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px',
    color: '#6c757d',
  },
  button: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#FF7F50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '4px',
  },
  buttonDisabled: {
    backgroundColor: '#9fa8c7',
    cursor: 'not-allowed',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '6px',
    marginTop: '16px',
    textAlign: 'center',
    fontSize: '14px',
  },
  messageSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: '1px solid #a7f3d0',
  },

  messageError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #fca5a5',
  },
  footer: {
    textAlign: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb',
  },
  footerText: { fontSize: '14px', color: '#495057', margin: '0' },
  link: {
    color: '#405189',
    textDecoration: 'none',
    fontWeight: '500',
  },
};

// Add CSS animation for spinner
const spinnerStyle = document.createElement('style');
spinnerStyle.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(spinnerStyle);
